import {
  type Static,
  type TArray,
  type TObject,
  type TObjectOptions,
  type TOptional,
  type TProperties,
  type TSchema,
  type TString,
  type TUnsafe,
  Type,
} from "typebox";

/**
 * The building blocks every contract is assembled from.
 *
 * Each is both a JSON Schema, which Fastify validates requests and serializes
 * responses with, and a TypeScript type, which the API handler and the web
 * screen are both checked against. Keeping one definition for both is the
 * point: a field renamed here breaks the build on each side of the wire.
 */

export const Identifier = Type.String({
  pattern:
    "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$",
});

/** A calendar date with no time or zone, such as a due date. */
export const DateOnly = Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" });

/**
 * An instant, as an ISO 8601 string. Always a string on the wire: a handler
 * holding a `Date` converts it, so the type the web reads is what arrives.
 */
export const Timestamp = Type.String({ format: "date-time" });

export const Money = Exact({
  amountCents: Type.Integer({ minimum: 0 }),
  currency: Type.String({ pattern: "^[A-Z]{3}$" }),
});

/**
 * An object that says exactly what it carries.
 *
 * `additionalProperties: false` matters for responses as much as requests:
 * the serializer then sends only the declared fields, so a handler cannot
 * leak a column nobody meant to publish, and a field missing from the schema
 * shows up in review rather than silently in production.
 */
export function Exact<Properties extends TProperties>(
  properties: Properties,
  options: Omit<TObjectOptions, "additionalProperties"> = {},
): TObject<Properties> {
  return Type.Object(properties, { ...options, additionalProperties: false });
}

/**
 * A shape built on another: a detail that is its summary plus more. Composing
 * rather than copying is what keeps a list item and its detail from drifting
 * apart, which is how the detail screen once read a field only the list sent.
 */
export function Extend<Base extends TProperties, Added extends TProperties>(
  base: TObject<Base>,
  properties: Added,
): TObject<Omit<Base, keyof Added> & Added> {
  return Exact({ ...base.properties, ...properties } as Omit<
    Base,
    keyof Added
  > &
    Added);
}

/**
 * A value that may be null.
 *
 * Scalars become `type: [..., "null"]` rather than an `anyOf`, matching how
 * the API has always declared them: the serializer handles a type list
 * directly, whereas `anyOf` makes it validate every value to pick a branch.
 */
export function Nullable<T extends TSchema>(
  schema: T,
): TUnsafe<Static<T> | null> {
  const source = schema as {
    readonly enum?: unknown[];
    readonly type?: unknown;
  };
  if (typeof source.type === "string" && source.type !== "object") {
    return Type.Unsafe<Static<T> | null>({
      ...schema,
      ...(source.enum ? { enum: [...source.enum, null] } : {}),
      type: [source.type, "null"],
    });
  }
  return Type.Unsafe<Static<T> | null>(Type.Union([schema, Type.Null()]));
}

/**
 * One of a fixed set of strings. Declared as `type: "string"` with an `enum`
 * so a response serializes as a plain string instead of through a union.
 */
export function StringEnum<const Values extends readonly [string, ...string[]]>(
  values: Values,
): TUnsafe<Values[number]> {
  return Type.Unsafe<Values[number]>({ enum: [...values], type: "string" });
}

/**
 * A JSON object whose keys are not known in advance, such as the before and
 * after of an audit event. Declared as a record rather than a bare object,
 * because `{ type: "object" }` alone serializes to `{}`.
 */
export const JsonRecord = Type.Record(Type.String(), Type.Unknown());

/** A plain list, for collections small enough to return whole. */
export function ListOf<Item extends TSchema>(
  item: Item,
): TObject<{ items: TArray<Item> }> {
  return Exact({ items: Type.Array(item) });
}

/**
 * A cursor-paginated list. The cursor is absent on the last page, so its
 * presence is the "load more" signal.
 */
export function Page<Item extends TSchema>(
  item: Item,
): TObject<{ cursor: TOptional<TString>; items: TArray<Item> }> {
  return Exact({
    cursor: Type.Optional(Type.String()),
    items: Type.Array(item),
  });
}

/** An offset-paginated list that reports how many there are in all. */
export function WithTotal<Item extends TSchema>(
  item: Item,
): TObject<{ items: TArray<Item>; total: ReturnType<typeof Type.Integer> }> {
  return Exact({ items: Type.Array(item), total: Type.Integer() });
}

export const FieldError = Exact({
  message: Type.String(),
  path: Type.String(),
});

/** What every failure answers with, whatever the route. */
export const ErrorEnvelope = Exact({
  error: Exact({
    code: Type.String(),
    correlationId: Type.String(),
    fields: Type.Optional(Type.Array(FieldError)),
    message: Type.String(),
  }),
});

export type ErrorEnvelope = Static<typeof ErrorEnvelope>;
export type FieldError = Static<typeof FieldError>;

/**
 * The error statuses a route can answer with, each carrying the envelope.
 * Spread into a contract's responses: `{ 200: Thing, ...errors(401, 404) }`.
 */
export function errors<const Statuses extends readonly number[]>(
  ...statuses: Statuses
): { readonly [Status in Statuses[number]]: typeof ErrorEnvelope } {
  return Object.fromEntries(
    statuses.map((status) => [status, ErrorEnvelope]),
  ) as { readonly [Status in Statuses[number]]: typeof ErrorEnvelope };
}

/** A response with no body, for 204. */
export const NoContent = Type.Null();
